import React from "react";
import T from "prop-types";
import { Map } from "immutable";
import { Link } from "react-router-dom";
import { dollars } from "LIB/formatters/currency";
import { shortDate } from "LIB/formatters/date";
import { withFormFields } from "MODULES/shared/decorators";
import { CUSTOMER_RMA_REASONS } from "MODULES/RMAsAndWarranties/constants";
import * as F from "MODULES/shared/components/Forms/FormTypes";
import { PartNumber } from "MODULES/shared/components";
import { NAME, QUANTITY, UUID } from "APP_ROOT/constants";
import { PRICE_PER_UNIT } from "MODULES/OutgoingQuotes/constants";
import { BIO_HAZMAT, HAZMAT, PART } from "MODULES/parts/constants";
import {
  CORE_RETURN_SERIAL_NUMBER,
  INVENTORY,
  LOCATION,
  STORE_LOCATION,
  STORE_OWNER,
  TAG_DATE,
  TAGGED_BY,
  TRACE_TYPE,
  TRACED_TO
} from "MODULES/inventories/constants";
import {
  CANCELLATION_REASONS,
  CORE_REPAIR_INVOICE_NUMBER,
  CORE_REPAIR_INVOICE_PAID,
  CUMULATIVE_COST_PER_UNIT
} from "MODULES/SalesOrders/constants";
import Cost from "./Cost";
import IncomingAWB from "./IncomingAWB";
import ExchangeLink from "./ExchangeLink";

function calculateCost(qty = 0, pricePerUnit = 0, exchangeFee = 0, cost = 0) {
  const baseTotal =
    parseFloat(qty) * parseFloat(pricePerUnit) || parseFloat(cost);
  return dollars(parseFloat(exchangeFee) || baseTotal);
}

function hazmatWarnings(isHazmat, isBioHazmat) {
  const warnings = [];
  if (isHazmat) {
    warnings.push("Hazmat");
  }
  if (isBioHazmat) {
    warnings.push("Bio Hazmat");
  }
  if (warnings.length === 0) {
    return false;
  }

  return (
    <div className="alert alert-warning" role="alert">
      <div className="media">
        <div className="media-left">
          <i className="fa fa-lg fa-warning text-warning" />
        </div>
        <div className="media-body">
          <strong>Warning!</strong> This part is marked: {warnings.join(", ")}
        </div>
      </div>
    </div>
  );
}

function Summary({
  inventory,
  lineItem,
  outgoing_awb_number: { input: { value: outgoingAWB } },
  exchange_fee: { input: { value: exchangeFee } },
  exchange_sale: { input: { value: exchangeSale } },
  core_return_date: { input: { value: coreReturnDate } }
}) {
  const part = lineItem.get(PART, Map());
  const reason = CANCELLATION_REASONS.find(
    x => x.value === lineItem.get("cancellation_reason")
  );

  return (
    <div className="well">
      <h6 className="h4">Part Summary</h6>
      <table className="table table-condensed table-padding-horizonal-none margin-bottom-none">
        <tbody>
          <tr>
            <td>
              Part #: <PartNumber part={part.toJS()} />
            </td>
          </tr>
          <tr>
            <td className="part-summary">
              <em className="text-muted">{part.get("description")}</em>
              <br />
              <br />
              <p>
                Serial #:{" "}
                <Link to={`/inventories/${inventory.get("uuid")}`}>
                  {lineItem.get("serial_number")}
                </Link>
              </p>
              <p>Quantity: {inventory.get(QUANTITY)}</p>
              <Cost inventory={inventory} lineItem={lineItem} />
              <p>
                Cost Per Unit: {dollars(lineItem.get(CUMULATIVE_COST_PER_UNIT))}
              </p>
              <p>Trace To: {inventory.get(TRACED_TO)}</p>
              <p>Trace Type: {inventory.get(TRACE_TYPE)}</p>
              <p>Tagged By: {inventory.get(TAGGED_BY)}</p>
              <p>Tag Date: {shortDate(inventory.get(TAG_DATE))}</p>
              <p>Store Owner: {inventory.getIn([STORE_OWNER, NAME])}</p>
              <p>Store Location: {inventory.getIn([STORE_LOCATION, NAME])}</p>
              <p>Location: {inventory.get(LOCATION)}</p>
              <br />
              <p>Date Out: {shortDate(lineItem.get("shipped_at"))}</p>
              <IncomingAWB lineItem={lineItem} />
              {lineItem.get("customer_rmas") &&
                lineItem.get("customer_rmas").toJS().length > 0 && (
                  <p>
                    RMA Reason:{" "}
                    {lineItem.get("customer_rmas").map(rma => (
                      <span>
                        {CUSTOMER_RMA_REASONS[rma.get("reason")].label}
                        <br />
                      </span>
                    ))}
                  </p>
                )}
              {lineItem.get("cancelled") && (
                <p>
                  <br />
                  <span style={{ color: "#f44336" }}>
                    Cancellation Reason:
                  </span>{" "}
                  {reason.text}
                  <br />
                </p>
              )}
              <ExchangeLink
                item={lineItem.get("parent_exchange_item")}
                title="Original Sales Order"
              />
              <ExchangeLink
                item={lineItem.get("second_exchange_item")}
                title="2nd Exchange"
              />
              <ExchangeLink
                item={lineItem.get("third_exchange_item")}
                title="3rd Exchange"
              />
              <ExchangeLink
                item={lineItem.get("reference_exchange_sale_for_outright")}
                title="Ref. Exch. SO: "
              />
            </td>
          </tr>
        </tbody>
      </table>
      <br />
      {hazmatWarnings(
        lineItem.getIn([PART, HAZMAT]),
        lineItem.getIn([PART, BIO_HAZMAT])
      )}
      {!!exchangeSale && [
        <hr className="hr-xs" />,
        <h6 className="h4">Core Return</h6>,
        <table className="table table-padding-horizonal-none">
          <tbody>
            <tr>
              <td className="part-summary">
                <p>Outgoing AWB #: {outgoingAWB}</p>
                <p>Core Return Date: {shortDate(coreReturnDate)}</p>
                <p>
                  Core Repair Charges:{" "}
                  {dollars(lineItem.get("core_repair_charges"))}
                </p>
                <p>
                  Core Return Serial Number:{" "}
                  <Link
                    to={`/inventories/${lineItem.getIn([INVENTORY, UUID])}`}
                  >
                    {lineItem.get(CORE_RETURN_SERIAL_NUMBER)}
                  </Link>
                </p>
                <p>
                  Core Repair Inv. #: {lineItem.get(CORE_REPAIR_INVOICE_NUMBER)}
                </p>
                <p>
                  Repair Inv. Paid Date:{" "}
                  {shortDate(lineItem.get(CORE_REPAIR_INVOICE_PAID))}
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      ]}
      <hr className="hr-xs" />
      <table className="table table-padding-horizonal-none">
        <tbody>
          <tr>
            <td>
              <h5 className="h4 margin-top-none margin-bottom-none">Total</h5>
            </td>
            <td className="text-right">
              <h5 className="h4 margin-top-none margin-bottom-none">
                {calculateCost(
                  inventory.get(QUANTITY),
                  lineItem.get(PRICE_PER_UNIT),
                  !!exchangeSale && exchangeFee,
                  inventory.get("total_cost")
                )}
              </h5>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

Summary.propTypes = {
  inventory: T.instanceOf(Map),
  lineItem: T.instanceOf(Map),
  outgoing_awb_number: F.formField(F.stringInput).isRequired,
  exchange_fee: F.formField(F.stringInput).isRequired,
  exchange_sale: F.formField(F.boolInput).isRequired,
  core_return_date: F.formField(F.stringInput).isRequired
};

export default withFormFields(Summary, [
  "outgoing_awb_number",
  "exchange_fee",
  "core_return_date",
  "exchange_sale"
]);



// WEBPACK FOOTER //
// ./app/modules/SalesOrders/components/Detail/Body/LineItems/Form/Summary/Summary.jsx